import React, { createContext, useContext, useState, useCallback } from 'react';

interface AISuggestion {
  id: string;
  original: string;
  suggested: string;
  isLoading: boolean;
  expIndex: number;
  descId: string;
}

interface AIContextType {
  suggestions: AISuggestion[];
  selectedBulletPoint: AISuggestion | null;
  expandedBulletPoint: { expIndex: number; descId: string } | null;
  
  // Actions
  setSuggestions: (suggestions: AISuggestion[]) => void;
  setSelectedBulletPoint: (suggestion: AISuggestion | null) => void;
  setExpandedBulletPoint: (point: { expIndex: number; descId: string } | null) => void;
  
  // AI-specific actions
  startAIProcessing: (expIndex: number, descId: string) => void;
  finishAIProcessing: (expIndex: number, descId: string, suggestion: string) => void;
  clearAIState: () => void;
}

const AIContext = createContext<AIContextType | null>(null);

export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within AIProvider');
  }
  return context;
};

interface AIProviderProps {
  children: React.ReactNode;
}

export const AIProvider: React.FC<AIProviderProps> = ({ children }) => {
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  const [selectedBulletPoint, setSelectedBulletPoint] = useState<AISuggestion | null>(null);
  const [expandedBulletPoint, setExpandedBulletPoint] = useState<{ expIndex: number; descId: string } | null>(null);

  const startAIProcessing = useCallback((expIndex: number, descId: string) => {
    const newSuggestion: AISuggestion = {
      id: `${expIndex}-${descId}`,
      original: '',
      suggested: '',
      isLoading: true,
      expIndex,
      descId
    };
    
    setSelectedBulletPoint(newSuggestion);
    setExpandedBulletPoint({ expIndex, descId });
  }, []);

  const finishAIProcessing = useCallback((expIndex: number, descId: string, suggestion: string) => {
    setSelectedBulletPoint(prev => 
      prev ? { ...prev, suggested: suggestion, isLoading: false } : null
    );
  }, []);

  const clearAIState = useCallback(() => {
    setSelectedBulletPoint(null);
    setExpandedBulletPoint(null);
  }, []);

  const value: AIContextType = {
    suggestions,
    selectedBulletPoint,
    expandedBulletPoint,
    setSuggestions,
    setSelectedBulletPoint,
    setExpandedBulletPoint,
    startAIProcessing,
    finishAIProcessing,
    clearAIState
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}; 